import './connectHeader.css';

import React, { useCallback, useEffect, useRef } from 'react';
import { href, useBuddi } from '@bentley/pw-config';
import { useConfigContext } from '../../context';
import { SecondaryHeader } from './secondaryHeader';

declare const window: {
  GlobalHeaderFooter?: {
    setup: (config: unknown) => void;
  };
};

type ConnectHeaderProps = {
  contextId?: string;
  customHeaderOptions?: Record<string, unknown>;
  dataAccessLevel: 'Datasource' | 'WorkArea';
  showSecondaryHeader?: boolean;
  userEmail: string;
};

export function ConnectHeader({
  contextId,
  customHeaderOptions,
  dataAccessLevel,
  showSecondaryHeader = true,
  userEmail
}: ConnectHeaderProps): JSX.Element {
  const { config } = useConfigContext();

  const connectPortalUrl = useBuddi('ConnectPortalUrl', config.BUDDIRegionCode);

  const headerInitialized = useRef(false);

  function getHeaderFooterScriptElement(): HTMLScriptElement {
    const scriptElement = document.querySelector(
      'script[data-testid="header-footer-script"]'
    );

    if (scriptElement instanceof HTMLScriptElement) {
      scriptElement.remove();
    }

    const headerFooterScript = document.createElement('script');
    headerFooterScript.setAttribute('data-testid', 'header-footer-script');
    return headerFooterScript;
  }

  const setupHeaderFooter = useCallback((): void => {
    if (headerInitialized.current) {
      return;
    }

    window.GlobalHeaderFooter?.setup({
      Email: userEmail,
      CurrentProjectId: contextId,
      HelpUrl:
        dataAccessLevel == 'Datasource' ? href.pWWebViewHelp : href.pWWebHelp,
      LegalUrl: href.legal,
      SignOutUrl: '/logout',
      CustomHeaderOptions: customHeaderOptions
    });

    headerInitialized.current = true;
  }, [contextId, customHeaderOptions, dataAccessLevel, userEmail]);

  useEffect(() => {
    if (!connectPortalUrl) {
      return;
    }

    const headerFooterScript = getHeaderFooterScriptElement();
    headerFooterScript.onload = setupHeaderFooter;
    headerFooterScript.src = `${connectPortalUrl}/Scripts/header-footer.js`;

    document.head.appendChild(headerFooterScript);
  }, [connectPortalUrl, setupHeaderFooter]);

  return (
    <>
      <nav id="GlobalHeader" data-testid="GlobalHeader" />
      {showSecondaryHeader && <SecondaryHeader />}
    </>
  );
}
